/**
 * Thistle Predictive Search Custom Element
 * Extends SearchForm to provide multi-column predictive search powered by Shopify native search.
 */

class ThistlePredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allInstances = document.querySelectorAll('thistle-predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.statusElement = this.querySelector('.predictive-search-status');
    this.loadingText = this.getAttribute('data-loading-text') || 'Loading...';

    this.setupPredictiveListeners();
  }

  setupPredictiveListeners() {
    if (!this.input) return;

    this.input.form?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this.close();
      }
    });
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onChange() {
    super.onChange();
    const query = this.getQuery();

    if (!query.length) {
      this.close(true);
      return;
    }

    this.searchTerm = query;
    this.getSearchResults(query);
  }

  onFormSubmit(event) {
    const selectedLink = this.querySelector('[aria-selected="true"] a');
    if (selectedLink) {
      event.preventDefault();
      selectedLink.click();
      return;
    }

    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const query = this.getQuery();
    if (!query.length) return;

    if (this.searchTerm !== query) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    }, 150);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const currentSelected = this.querySelector('[aria-selected="true"]');
    const allOptions = Array.from(this.querySelectorAll('.thistle-predictive-search__item, .thistle-predictive-search__search-for-button'))
      .filter((el) => el.offsetParent !== null);

    if (!allOptions.length) return;

    let nextIndex = 0;
    const currentIndex = currentSelected ? allOptions.indexOf(currentSelected) : -1;

    if (direction === 'down') {
      nextIndex = currentIndex < allOptions.length - 1 ? currentIndex + 1 : 0;
    } else if (direction === 'up') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : allOptions.length - 1;
    }

    if (currentSelected) {
      currentSelected.setAttribute('aria-selected', 'false');
    }

    const nextOption = allOptions[nextIndex];
    if (nextOption) {
      nextOption.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', nextOption.id);
    }
  }

  selectOption() {
    const selected = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selected) {
      selected.click();
    }
  }

  getSearchResults(searchTerm) {
    const cacheKey = searchTerm.toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[cacheKey]) {
      this.renderSearchResults(this.cachedResults[cacheKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    // Use storefront search (same engine + prefix options as /search) so dropdown
    // ranking matches the search results page. Predictive suggest ranks differently.
    const searchUrl = `${window.routes?.search_url || '/search'}?q=${encodeURIComponent(
      searchTerm
    )}&type=product,collection,page&options[prefix]=last&section_id=thistle-predictive-search`;

    fetch(searchUrl, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((htmlText) => {
        const parsed = new DOMParser().parseFromString(htmlText, 'text/html');
        const sectionContent = parsed.querySelector('#shopify-section-thistle-predictive-search')?.innerHTML || '';

        this.cachedResults[cacheKey] = sectionContent;
        this.allInstances.forEach((inst) => {
          inst.cachedResults[cacheKey] = sectionContent;
        });

        this.renderSearchResults(sectionContent);
      })
      .catch((error) => {
        if (error.code !== 20 && error.name !== 'AbortError') {
          this.close();
          console.error('Predictive search error:', error);
        }
      });
  }

  setLiveRegionLoadingState() {
    this.setAttribute('loading', 'true');
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = this.loadingText;
    }
  }

  renderSearchResults(html) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = html;
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');

    const liveCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (this.statusElement && liveCount) {
      this.statusElement.textContent = liveCount.textContent;
    }

    this.open();
  }

  open() {
    this.setAttribute('open', 'true');
    this.input?.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clear = false) {
    this.closeResults(clear);
    this.isOpen = false;
  }

  closeResults(clear = false) {
    if (clear && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const currentSelected = this.querySelector('[aria-selected="true"]');
    if (currentSelected) {
      currentSelected.setAttribute('aria-selected', 'false');
    }

    this.input?.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('thistle-predictive-search', ThistlePredictiveSearch);
